import { Button, Sessions, useAuth, UserLogin } from "@app/shared";
import { FC, useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { Pages } from "..";
import { useModel } from "../../../hooks";
import { NoAuthAccountLayout } from "../../layouts";
import { LoginFormFields } from "../../shared/LoginFormFields";

export const Login: FC = (props) => {
    const [credentials, { bindingsFor, validateWithAction, modelContainerClassNames, ErrorSummary }] = useModel(UserLogin);
    const navigate = useNavigate();
    const [query] = useSearchParams();
    const ssoError = query.get("ssoError");
    const submitButton = useRef<HTMLButtonElement>(null);
    const { logIn, logOut, isAuthenticated, isLoading } = useAuth();
    const [ssoEnabled, setSsoEnabled] = useState(false);

    useEffect(() => {
        let cancelled = false;
        Sessions.ssoStatusInfo()
            .then((status) => { if (!cancelled && status?.enabled) setSsoEnabled(true); })
            .catch(() => { /* leave SSO hidden if status check fails */ });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (isAuthenticated && !isLoading) {
            logOut();
        }
    }, [isLoading, isAuthenticated, logOut]);

    async function handleLogInOnClick() {
        await validateWithAction(async () => {
            await logIn(credentials as UserLogin);
            if (query.has("fwd")) {
                navigate(query.get("fwd")!);
            } else {
                navigate("/");
            }
        });
    }

    function handleSsoLoginOnClick() {
        const returnUrl = query.get("fwd") ?? "/";
        const apiRoot = (process.env.REACT_APP_CORE_API_ROOT_URL ?? "").replace(/\/$/, "");
        window.location.href = `${apiRoot}/api/session/sso/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    }

    return (
        <NoAuthAccountLayout>
            <div className="mx-auto w-full max-w-sm my-10">
                <div className={`${modelContainerClassNames} flex justify-center justify-items-center flex-col`}>
                    <div className="mx-auto mb-3">
                        <h1>Sign In</h1>
                    </div>

                    <LoginFormFields emailBindings={bindingsFor("email")} passwordBindings={bindingsFor("password")} ErrorSummary={ErrorSummary} onPasswordEnter={() => submitButton.current?.click()} showErrorSummary={true} />

                    {!!ssoError && (
                        <div className="mx-auto mt-2 text-red-600 text-sm text-center max-w-sm">{ssoError}</div>
                    )}

                    <div className="mx-auto mt-2">
                        <Button ref={submitButton} type="submit" onClick={handleLogInOnClick}>
                            Log In
                        </Button>
                    </div>
                    {ssoEnabled && (
                        <div className="mx-auto mt-2">
                            <Button type="button" onClick={handleSsoLoginOnClick}>Sign in with SSO</Button>
                        </div>
                    )}
                </div>
                <div className="mx-auto text-center mt-10">
                    <NavLink to={Pages.ForgotPassword.getUrl()}>Forgot your password?</NavLink>
                    <br />
                    <NavLink to={Pages.CreateAccount.getUrl()}>Create a new account</NavLink>
                    <br />
                    <NavLink to={Pages.Terms.getUrl()}>Terms and Conditions</NavLink>
                </div>
            </div>
        </NoAuthAccountLayout>
    );
};
